<template>
  <div class="request-list-table" data-testid="request-list-table">
    <div class="d-flex align-center justify-end mb-2">
      <!-- Search -->
      <div class="d-none">
        <v-slide-x-reverse-transition>
          <lb-string
            v-if="showSearch"
            width="30vh"
            :value="searchQuery"
            @input="$emit('update:searchQuery', $event)"
            placeholder="Search.."
            hidedetails
            label=""
            :isClearable="false"
            data-testid="request-list-table__search-input"
          />
        </v-slide-x-reverse-transition>
        <v-tooltip bottom content-class="tooltip-bottom">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              @click="showSearch = !showSearch"
              data-testid="request-list-table__search-btn"
            >
              <v-icon>mdi-magnify</v-icon>
            </v-btn>
          </template>
          <span>Search</span>
        </v-tooltip>
      </div>
      <!-- Send Status Summary Email -->
       <base-button label="Send Status Summary Email" primary image="/img/icons/EmailIcon.svg"  v-if="selectedItems.length > 0 && isManager" :loading="summaryEmailLoading" @click="$emit('summary-email')" class="EmailBtn" data-testid="request-list-table__send-summary-email-btn"/>
      <!-- Bulk Actions -->
      <v-menu
        :close-on-content-click="false"
        offset-y
        transition="slide-y-transition"
        v-if="selectedItems.length > 0"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-tooltip bottom content-class="tooltip-bottom">
            <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
              <v-btn
                icon
                v-bind="{ ...attrs, ...tooltipAttrs }"
                v-on="{ ...on, ...tooltipOn }"
                data-testid="request-list-table__bulk-actions-btn"
              >
                <v-icon>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>
            <span>Bulk Actions</span>
          </v-tooltip>
        </template>
        <v-list class="pa-0 ma-0 d-flex flex-column all-wdth align-start justify-start all-width">
          <v-list-item class="tableHeaderBg all-width font-weight-bold">Bulk Actions</v-list-item>
          <v-list-item @click="$emit('action:bulk-edit-owner')" data-testid="request-list-table__bulk-edit-owner">
            <v-icon class="mr-2">mdi-pencil-outline</v-icon>
            <span>Edit Information owner</span>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- Clear Filter Button - First item before CustomizeColumnMenu -->
      <v-btn
        v-if="hasActiveTableFilter"
        outlined
        small
        class="mr-2"
        color="caccounting"
        @click="clearTableFilter()"
        data-testid="request-list-table__toolbar-clear-filter-btn"
      >
        <v-icon left data-testid="request-list-table__toolbar-clear-filter-icon">mdi-filter-remove</v-icon>
        <span>Clear Filter</span>
      </v-btn>
       <!-- Information Owner Summary -->
      <v-tooltip bottom content-class="tooltip-bottom">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            icon
            large
            @click="$emit('action:info-owner-summary')"
            data-testid="request-list-table__info-owner-summary-btn"
          >
            <v-icon>mdi-newspaper-variant-outline</v-icon>
          </v-btn>
        </template>
        <span>Information Owner</span>
      </v-tooltip>
      <!-- CustomizeColumnMenu component - Apply only, emits to parent -->
      <CustomizeColumnMenu
        v-model="customizeMenuOpen"
        :headers="headers"
        color="caccounting"
        :show-save-button="false"
        :show-apply-button="true"
        data-testid-prefix="request-list-table"
        @apply="handleApplyColumns"
      />
    </div>

    <v-card-text v-if="loading" data-testid="request-list-table__loading-spinner">
      <v-skeleton-loader
        type="table-heading, list-item-two-line, article, article"
        data-testid="request-list-table__loading-skeleton"
      ></v-skeleton-loader>
    </v-card-text>

    <div class="request-list-table__table-area" v-else>
      <lb-datatableaction
        ref="dataTableAction"
        v-if="requestList.length > 0 || originalRequestCount > 0"
        :headers="headers"
        :tabledata="requestList"
        :original-data="originalRequestList"
        :filter-options-data="columnFilterOptionsData"
        :itemsperpage="itemsPerPage"
        :searchterm="searchQuery"
        :tableHeight="tableHeight || undefined"
        @select-all-change="onSelectAllChange"
        :isDataSelected="selectedItems.length > 0 && selectedItems.length < paginatedItems.length"
        @filter-item="$emit('filter-item', $event)"
        @filter-active-change="onFilterActiveChange"
        @clear-item="$emit('clear-item', $event)"
        @column-reorder="handleColumnReorder"
        :selectfilter="selectFilter"
        :page_details="pageDetails"
        data-testid="request-list-table__datatable"
      >
        <template v-slot:tbody>
          <tr
            v-for="(item, index) in paginatedItems"
            :key="item._id || index"
            class="cursor-pointer"
            :class="{ 'low-opacity': item.status === false }"
            :data-testid="`request-list-table__row--${index}`"
          >
            <td class="px-2 text-center checkbox-cell">
              <v-checkbox
                dense
                color="caccounting"
                class="ma-0 py-1 single-line"
                :ripple="false"
                hide-details
                :value="item._id"
                :input-value="selectedItems"
                @change="toggleSelection(item._id)"
                multiple
                :data-testid="`request-list-table__row-checkbox--${index}`"
              ></v-checkbox>
            </td>
            <template v-for="header in headers">
              <td
                v-if="header.visible && header.value !== 'action'"
                :key="header.value + '-' + (item._id || index)"
                class="px-2 single-line"
                :class="{
                  'description-header': header.value === 'description',
                  'single-line': header.value !== 'description',
                }"
                :data-testid="`request-list-table__cell--${header.value}-row-${index}`"
              >
                <span v-if="header.value === 'index'">{{ (currentPage - 1) * itemsPerPage + index + 1 }}</span>

                <div v-else-if="header.value === 'stage'">
                  <v-chip
                    class="pending-sub-chip statusChipHeader pa-0"
                    v-if="item[header.value] === 0"
                    data-testid="request-list-table__stage-chip--pending-submission"
                  >
                    <span class="status-empty-indicator status-indicator-width-height-10"></span>
                    <span data-testid="request-list-table__stage-chip-label--pending-submission">Pending Submission</span>
                  </v-chip>
                  <v-chip
                    class="pending-approval-chip statusChipHeader pa-0"
                    v-if="item[header.value] === 1"
                    data-testid="request-list-table__stage-chip--pending-approval"
                  >
                    <span class="status-empty-indicator status-indicator-width-height-10"></span>
                    <span data-testid="request-list-table__stage-chip-label--pending-approval">Pending Approval</span>
                  </v-chip>
                  <v-chip
                    class="cancelled-chip statusChipHeader pa-0"
                    v-if="item[header.value] === -1"
                    data-testid="request-list-table__stage-chip--cancelled"
                  >
                    <span class="status-empty-indicator status-indicator-width-height-10"></span>
                    <span data-testid="request-list-table__stage-chip-label--cancelled">Cancelled</span>
                  </v-chip>
                  <v-chip
                    class="closed-task-chip statusChipHeader pa-0"
                    v-if="item[header.value] === 2"
                    data-testid="request-list-table__stage-chip--closed"
                  >
                    <span class="status-empty-indicator status-indicator-width-height-10"></span>
                    <span data-testid="request-list-table__stage-chip-label--closed">Approved</span>
                  </v-chip>
                </div>
                <div v-else-if="header.value === 'priority'">
                  <v-chip
                    label
                    class="lightgray radius-small"
                    v-if="item.priority"
                    data-testid="request-list-table__priority-chip--row"
                  >
                    <span>{{ item.priority }}</span>
                  </v-chip>
                </div>
                <div v-else-if="header.value === 'info_owner_name'">
                  {{ (item.info_owner_detail || {}).name || (item.info_owner_detail || {}).email }}
                </div>
                <span v-else-if="header.value === 'created_by_name'">
                  {{ item.created_by_name || 'N/A' }}
                </span>
                <span v-else-if="header.value === 'ref'">
                  <span v-if="item.referenceChecklist && Array.isArray(item.referenceChecklist) && item.referenceChecklist.length > 0" :data-testid="`request-list-table__reference-value--row-${index}`">
                    <template v-for="(refItem, refIdx) in item.referenceChecklist">
                      <span :key="`ref-${item._id || index}-${refItem._id || refIdx}`">{{ refItem.ref }}</span>
                      <span v-if="refIdx < item.referenceChecklist.length - 1" :key="`ref-comma-${item._id || index}-${refItem._id || refIdx}`">, </span>
                    </template>
                  </span>
                </span>
                <span v-else-if="header.value === 'duedate_days'">
                  <div v-if="item.duedate">
                    <v-tooltip bottom content-class="tooltip-bottom">
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip
                          label
                          size="25"
                          :color="getDueDateColor(item.duedate)"
                          :class="getDueDateTextClass(item.duedate)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <span>{{ getDueDateDays(item.duedate) }}</span>
                        </v-chip>
                      </template>
                      <span>
                        {{ getDueDateLabel(item.duedate) }}
                      </span>
                    </v-tooltip>
                  </div>
                </span>
                <span v-else-if="header.value === 'location'">
                  {{ getLocationName(item.location) }}
                </span>
                <span v-else-if="header.value === 'created_at'">
                  {{ $nova.formatDateTime(new Date(item.created_at), 'DD MMM yyyy') }}
                </span>
                <span v-else-if="header.value === 'updated_at'">
                  {{ $nova.formatDateTime(new Date(item.updated_at), 'DD MMM yyyy') }}
                </span>
                <span v-else> {{ item[header.value] }}</span>
              </td>
            </template>
            <td class="d-flex align-center px-3 single-line border-right border-left action-data">
              <div class="d-flex align-center justify-center">
                <v-menu bottom offset-y :max-width="400" elevation-0 :close-on-content-click="false">
                  <template v-slot:activator="{ on, attrs }">
                    <div class="text-center">
                      <v-btn icon v-bind="attrs" v-on="on" data-testid="request-list-table__row-actions-btn">
                        <v-icon>mdi-dots-horizontal</v-icon>
                      </v-btn>
                    </div>
                  </template>
                  <v-list style="width:25vh">
                    <!-- upload for pending submissions -->
                    <v-list-item class="d-flex pa-0 flex-column align-start justify-center px-1">
                      <v-list-item
                        class="cursor-pointer hoverable-caccounting all-width radius-small"
                        :key="item._id ? 'action-upload-' + item._id : index"
                        v-if="item.stage === 0"
                        @click="$emit('action:upload', item)"
                        data-testid="request-list-table__action-upload"
                      >
                        <v-icon class="pr-2">mdi-file-upload-outline</v-icon>
                        <span>Upload</span>
                      </v-list-item>

                      <!-- download for pending approval -->
                      <v-list-item
                        class="all-width"
                        v-if="(item.stage === 1 || item.stage === 2 || item.stage === -1) && item.type === 'fileupload'"
                      >
                        <v-menu offset-x transition="slide-x-transition" :close-on-content-click="false">
                          <template v-slot:activator="{ on, attrs }">
                            <div
                              class="cursor-pointer all-width all-height radius-small"
                              v-bind="attrs"
                              v-on="on"
                              data-testid="request-list-table__action-download"
                            >
                              <v-icon class="pr-2">mdi-download</v-icon>
                              <span>Download</span>
                            </div>
                          </template>

                          <v-list
                            style="max-height:50vh; overflow:hidden; z-index:13 !important; width:20vh"
                            class="pa-0 ma-0 d-flex flex-column align-start justify-start all-width"
                          >
                            <v-list-title
                              class="all-width d-flex tableHeaderBg align-center pa-2 pb-3 pt-3 pb-1 justify-space-between"
                            >
                              Download All
                              <div class="d-flex align-center justify-end" v-if="resolveFileAttachments(item).length > 1">
                                  <v-btn
                                    small
                                    color=""
                                    class="pa-1 ma-auto"
                                    @click="downloadAllAttachments(item)"
                                    data-testid="request-list-table__download-all-btn"
                                  >
                                    <v-icon small>mdi-download-multiple</v-icon>
                                  </v-btn>
                                </div>
                            </v-list-title>
                            <v-divider></v-divider>
                            <v-list-item class="d-flex all-width align-start pa-0 ma-0 flex-column">
                              <template v-if="resolveFileAttachments(item).length > 0">
                                <v-list-item
                                  v-for="(file, fileIdx) in resolveFileAttachments(item)"
                                  :key="resolveFileKey(file, fileIdx)"
                                  class="d-flex pa-0 px-2 ma-0 all-width cursor-pointer request-list-table__file-item"
                                  :data-testid="`request-list-table__file-row--${fileIdx}`"
                                  role="button"
                                  tabindex="0"
                                  @click="downloadAttachment(file, item)"
                                  @keydown.enter="downloadAttachment(file, item)"
                                  @keydown.space.prevent="downloadAttachment(file, item)"
                                >
                                  <div class="d-flex align-center justify-space-between all-width">
                                    <span class="single-line">{{ file.originalname || file.uploadname || file.file_name || `Document ${fileIdx + 1}` }}</span>
                                    <v-icon small class="ml-2">mdi-download</v-icon>
                                  </div>
                                </v-list-item>
                              </template>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </v-list-item>

                      <!-- closed req view response-->
                      <v-list-item
                        class="cursor-pointer hoverable-caccounting all-width radius-small"
                        v-if="item.stage === -1 || item.stage === 2"
                        :key="item._id ? 'action-view-response-' + item._id : index"
                        @click="$emit('action:view-response', item)"
                        data-testid="request-list-table__action-view-response"
                      >
                        <v-icon class="pr-2">mdi-eye</v-icon>
                        <span>View Response</span>
                      </v-list-item>

                      <!-- approve pending approval req -->
                      <v-list-item
                        class="cursor-pointer hoverable-caccounting all-width radius-small"
                        v-if="item.stage === 1"
                        :key="item._id ? 'action-approve-' + item._id : index"
                        @click="$emit('action:approve', item._id)"
                        data-testid="request-list-table__action-approve"
                      >
                        <v-icon class="pr-2" color="green">mdi-check-circle</v-icon>
                        <span>Approve</span>
                      </v-list-item>

                      <!-- reject pending approval req -->
                      <v-list-item
                        class="cursor-pointer hoverable-caccounting all-width radius-small"
                        v-if="item.stage === 1"
                        :key="item._id ? 'action-reject-' + item._id : index"
                        @click="$emit('action:reject', item._id)"
                        data-testid="request-list-table__action-reject"
                      >
                        <v-icon class="pr-2" color="error">mdi-close-circle</v-icon>
                        <span>Reject</span>
                      </v-list-item>

                      <!-- edit for pending approval -->
                      <v-list-item
                        class="cursor-pointer hoverable-caccounting all-width radius-small"
                        v-if="item.stage === 1"
                        :key="item._id ? 'action-add-edit-response-' + item._id : index"
                        @click="$emit('action:add-edit-response', item)"
                        data-testid="request-list-table__action-add-edit-response"
                      >
                        <v-icon class="pr-2">mdi-file-download-outline</v-icon>
                        <span>Add/ Edit Response</span>
                      </v-list-item>

                      <!-- conversation -->
                      <v-list-item class="cursor-pointer hoverable-caccounting all-width radius-small" :key="item._id ? 'action-comments-' + item._id : index">
                        <v-btn
                          @click="$emit('action:comments', { item, index })"
                          text
                          class="pa-0"
                          small
                          data-testid="request-list-table__action-comments"
                        >
                          <v-badge
                            color="error"
                            offset-x="10"
                            offset-y="10"
                            dot
                            v-if="
                              (item.comments || []).length > 0 &&
                                (((item.comments || [])[(item.comments || []).length - 1] || {}).readby || []).indexOf(
                                  $store.state.user.id
                                ) === -1
                            "
                          >
                            <v-icon class="">mdi-comment-text-multiple</v-icon>
                          </v-badge>
                          <v-icon v-else class="mr-1">mdi-comment-text-multiple</v-icon>
                          <span>Comments</span>
                        </v-btn>
                      </v-list-item>

                      <!-- send back to pending for closed req -->
                      <v-list-item
                        class="cursor-pointer hoverable-caccounting all-width radius-small"
                        :key="item._id ? 'action-restore-' + item._id : index"
                        v-if="item.stage === -1 || item.stage === 2"
                        @click="$emit('action:restore', item._id)"
                        data-testid="request-list-table__action-restore"
                      >
                        <v-icon class="pr-2">mdi-restore</v-icon>
                        <span>Send back to pending</span>
                      </v-list-item>

                      <v-divider class="pa-0 ma-0"></v-divider>

                      <!-- follow up for pending app & submissin-->
                      <v-list-item
                        class="cursor-pointer hoverable-caccounting all-width radius-small"
                        v-if="item.stage === 1 || item.stage === 0"
                        :key="item._id ? 'action-follow-up-' + item._id : index"
                        @click="$emit('action:follow-up', item)"
                        data-testid="request-list-table__action-follow-up"
                      >
                        <v-icon class="pr-2">mdi-calendar-clock-outline</v-icon>
                        <span>Follow Up</span>
                      </v-list-item>

                      <!-- edit for all req -->
                      <v-list-item
                        class="cursor-pointer hoverable-caccounting all-width radius-small"
                        :key="item._id ? 'action-edit-' + item._id : index"
                        @click="$emit('action:edit', item)"
                        data-testid="request-list-table__action-edit"
                      >
                        <v-icon class="pr-2">mdi-pencil-outline</v-icon>
                        <span>Edit Request</span>
                      </v-list-item>

                      <!-- activity log for all req -->
                      <v-list-item
                        class="cursor-pointer hoverable-caccounting all-width radius-small"
                        :key="item._id ? 'action-activity-log-' + item._id : index"
                        data-testid="request-list-table__action-activity-log"
                      >
                      
                        <activity-log
                          :resource-id="item._id"
                          label="Activity Log"
                          :row-description="item.description"
                          list-item-mode
                          data-testid="request-list-table__activity-log"
                        />
                      </v-list-item>

                      <!-- cancel req -->
                      <v-list-item
                        v-if="item.stage != -1"
                        class="cursor-pointer hoverable-caccounting all-width radius-small"
                        :key="item._id ? 'action-cancel-' + item._id : index"
                        @click="$emit('action:cancel', item._id)"
                        data-testid="request-list-table__action-cancel"
                      >
                        <v-icon class="pr-2">mdi-cancel</v-icon>
                        <span>Cancel Request</span>
                      </v-list-item>

                      <!-- delete req  -->
                      <v-list-item
                        class="cursor-pointer hoverable-caccounting all-width radius-small"
                        :key="item._id ? 'action-delete-' + item._id : index"
                        @click="$emit('action:delete', item._id)"
                        data-testid="request-list-table__action-delete"
                      >
                        <v-icon class="pr-2">mdi-delete</v-icon>
                        <span>Delete Request</span>
                      </v-list-item>
                    </v-list-item>
                  </v-list>
                </v-menu>
                <v-tooltip bottom content-class="tooltip-bottom" v-if="item.stage === 0">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      v-bind="attrs"
                      v-on="on"
                      @click="$emit('action:upload', item)"
                      data-testid="request-list-table__upload-btn"
                    >
                      <v-icon>mdi-upload</v-icon>
                    </v-btn>
                  </template>
                  <span>Upload</span>
                </v-tooltip>
              </div>
            </td>
          </tr>
        </template>
      </lb-datatableaction>

      <div v-else data-testid="request-list-table__empty-state">
        <v-alert type="info" text border="left" :value="true" data-testid="request-list-table__empty-alert">
          No Requests added yet.
        </v-alert>
      </div>
    </div>
  </div>
</template>

<script>
import {
  calculateDueDateDays,
  determineDueDateColor,
  determineDueDateTextClass,
  determineDueDateLabel,
  resolveLocationName,
} from '@/modules/financialclose/utils/informationManager';
import {
  collectAttachmentEntries,
  resolveAttachmentIds,
  resolveAttachmentKey as resolveAttachmentItemKey,
} from '@/modules/financialclose/utils/informationManagerComponentUtils';
import CustomizeColumnMenu from '../../../../components/CustomizeColumnMenu/CustomizeColumnMenu.vue';
import ActivityLog from '../../../../components/ActivityLog/ActivityLog.vue';

const REQUEST_LIST_ATTACHMENT_FIELD_ORDER = ['fileid', '_id', 'file_id', 'id', 'uploadid', 'upload_id'];

export default {
  name: 'RequestListTable',
  components: {
    CustomizeColumnMenu,
   ActivityLog
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    paginatedItems: {
      type: Array,
      default: () => [],
    },
    requestList: {
      type: Array,
      default: () => [],
    },
    headers: {
      type: Array,
      default: () => [],
    },
    itemsPerPage: {
      type: Number,
      default: 12,
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },
    selectFilter: {
      type: Object,
      default: () => ({}),
    },
    pageDetails: {
      type: Object,
      default: () => ({}),
    },
    locationList: {
      type: Array,
      default: () => [],
    },
    searchQuery: {
      type: String,
      default: '',
    },
    originalRequestCount: {
      type: Number,
      default: 0,
    },
    originalRequestList: {
      type: Array,
      default: () => [],
    },
    /**
     * Stage-scoped list for column filter options (pre-column-filter).
     * Null when omitted; empty arrays are not passed through so DataTableAction
     * falls back to tabledata until real data arrives.
     */
    filterOptionsList: {
      type: Array,
      default: null,
    },
    /**
     * List of reference objects to map refId to ref label
     */
    selectedRefList: {
      type: Array,
      default: () => [],
    },
    /**
     * Table height - can be a fixed value like '500px' or responsive like 'calc(100vh - 400px)'.
     * When not provided, uses the DataTableAction default.
     */
    tableHeight: {
      type: String,
      default: '',
    },
    /**
     * Current page number for serial number calculation
     */
    currentPage: {
      type: Number,
      default: 1,
    },
    /**
     * Whether the current user is a manager on this project.
     * Controls visibility of the Send Status Summary Email button.
     */
    isManager: {
      type: Boolean,
      default: false,
    },
    summaryEmailLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showSearch: false,
      searchColumn: '',
      customizeMenuOpen: false,
      hasActiveTableFilter: false,
    };
  },
  computed: {
    filteredHeaders() {
      const searchTerm = this.searchColumn.toLowerCase();
      return this.headers.filter(header => header.text.toLowerCase()?.includes(searchTerm));
    },
    columnFilterOptionsData() {
      return this.filterOptionsList?.length ? this.filterOptionsList : null;
    },
  },
  methods: {
    /**
     * Handler for filter-active-change event from DataTableAction
     */
    onFilterActiveChange(isActive) {
      this.hasActiveTableFilter = isActive;
    },
    /**
     * Clear table filters via DataTableAction ref
     */
    clearTableFilter() {
      if (this.$refs.dataTableAction) {
        this.$refs.dataTableAction.clearFilter();
      }
    },
    /**
     * Handle Apply button click from CustomizeColumnMenu
     * Emits updated headers to parent component
     * @param {Array} headers - Updated headers array with visibility changes
     */
    handleApplyColumns(headers) {
      this.$emit('update:headers', headers);
      this.customizeMenuOpen = false;
    },
    /**
     * Handle column reorder from DataTableAction drag-and-drop
     * Updates local headers and emits to parent component
     * @param {Array} reorderedHeaders - New header order from DataTableAction
     */
    handleColumnReorder(reorderedHeaders) {
      this.$emit('update:headers', reorderedHeaders);
    },
    /**
     * Download all attachments for a row (multi-file download)
     */
    downloadAllAttachments(row) {
      const files = this.resolveFileAttachments(row);
      const ids = files.reduce((acc, file) => {
        const resolved = resolveAttachmentIds(file, REQUEST_LIST_ATTACHMENT_FIELD_ORDER);
        if (resolved.length > 0) acc.push(...resolved);
        return acc;
      }, []);
      if (ids.length === 0) return;
      const nova = this.$nova;
      if (typeof nova?.downloadFiles === 'function') {
        nova.downloadFiles(ids);
      } else if (ids.length === 1) {
        nova.downloadFile(ids[0]);
      } else {
        // fallback: open each in new tab
        ids.forEach(id => nova.downloadFile(id));
      }
    },
    onSelectAllChange(isSelected) {
      this.$emit('select-all-change', isSelected);
    },
    toggleSelection(id) {
      let newSelection = [...this.selectedItems];
      if (newSelection.includes(id)) {
        newSelection = newSelection.filter(item => item !== id);
      } else {
        newSelection.push(id);
      }
      this.$emit('update:selectedItems', newSelection);
    },
    getDueDateDays(duedate) {
      return calculateDueDateDays(duedate);
    },
    getDueDateColor(duedate) {
      return determineDueDateColor(duedate);
    },
    getDueDateTextClass(duedate) {
      return determineDueDateTextClass(duedate);
    },
    getDueDateLabel(duedate) {
      return determineDueDateLabel(duedate);
    },
    getLocationName(id) {
      return resolveLocationName(id, this.locationList);
    },
    /**
     * Normalises attachment sources so multi-file uploads are rendered consistently.
     */
    resolveFileAttachments(row) {
      return collectAttachmentEntries(row || {});
    },
    /**
     * Generates a stable key for attachment rows.
     */
    resolveFileKey(file, index) {
      return resolveAttachmentItemKey(file, index);
    },
    /**
     * Handles single attachment download with graceful fallbacks.
     */
    downloadAttachment(file, row) {
      const nova = this.$nova;
      if (!nova) return;
      const candidateIds = resolveAttachmentIds(file, REQUEST_LIST_ATTACHMENT_FIELD_ORDER);
      if (candidateIds.length === 1) {
        nova.downloadFile(candidateIds[0]);
        return;
      }
      if (candidateIds.length > 1) {
        if (typeof nova.downloadFiles === 'function') {
          nova.downloadFiles(candidateIds);
          return;
        }
        nova.downloadFile(candidateIds[0]);
        return;
      }
      if (Array.isArray(row?.fileid)) {
        const ids = row.fileid.filter(Boolean);
        if (ids.length === 1) nova.downloadFile(ids[0]);
        else if (ids.length > 1 && typeof nova.downloadFiles === 'function') nova.downloadFiles(ids);
      }
    },
    /**
     * Returns the ref label (item.ref) for a given reference ID, if available in selectedRefList
     */
    getRefLabel(refId) {
      if (!this.selectedRefList || !Array.isArray(this.selectedRefList)) return null;
      const found = this.selectedRefList.find(r => r.value === refId);
      return found ? found.ref : null;
    },
  },
};
</script>

<style lang="scss" scoped>
.request-list-table {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;

  &__table-area {
    flex: 1 1 0;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
}

.checkbox-cell {
  width: 3rem;
  vertical-align: middle;

  .v-input--checkbox {
    display: inline-flex;
    justify-content: center;
  }
}
::v-deep .EmailBtn img {
  filter: brightness(100) saturate(0%) !important;
}

</style>